import { Formik, Form } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { FaCheckDouble } from "react-icons/fa6"
import { useNavigate, useLocation } from 'react-router-dom'
import PageHeader from '../../components/Shared/PageHeader'
import TextInput from '../../components/Shared/TextInput'
import SharedButton from '../../components/Shared/SharedButton'
import validSupergitHIS from '../../api/validSupergitHIS'
import toast from 'react-hot-toast'

function PaymentMethodsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const editData = location?.state?.editData || null

  const initialValues = {
    name_en: editData?.name_en || '',
    name_ar: editData?.name_ar || '',
    short_name: editData?.short_name || ''
  }

  const validationSchema = Yup.object().shape({
    name_en: Yup.string()
      .required(t('validation.required'))
      .max(100, t('validation.maxLength', { max: 100 })),
    name_ar: Yup.string()
      .required(t('validation.required'))
      .max(100, t('validation.maxLength', { max: 100 })),
    short_name: Yup.string()
      .required(t('validation.required'))
      .max(20, t('validation.maxLength', { max: 20 }))
  })

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editData) {
        const res = await validSupergitHIS.put(`payment_methods/${editData?.id}`, values)
        if (res?.data) {
          toast.success(res?.data?.message || t('Updated successfully'))
        }
      } else {
        const res = await validSupergitHIS.post('payment_methods', values)
        if (res?.data) {
          toast.success(res?.data?.message || t('Created successfully'))
        }
      }
      navigate(-1)
      resetForm()
    } catch (error) {
      console.error('Submission error:', error)
      toast.error(t('Something went wrong'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col w-full gap-2 max-h-[calc(100vh-150px)]">
      <PageHeader 
        title={editData ? `${t('edit')} ${t('payment_method')}` : `${t('create')} ${t('payment_method')}`} 
        backPath={-1} 
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => (
          <Form className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2'>
              <TextInput
                label={t('name_en')}
                name='name_en'
                placeholder={`${t('enter')} ${t('name_en')}`}
                error={formik.errors.name_en && formik.touched.name_en}
              />

              <TextInput
                label={t('name_ar')}
                name='name_ar'
                placeholder={`${t('enter')} ${t('name_ar')}`}
                error={formik.errors.name_ar && formik.touched.name_ar}
              />

              <TextInput
                label={t('short_name')}
                name='short_name'
                placeholder={`${t('enter')} ${t('short_name')}`}
                error={formik.errors.short_name && formik.touched.short_name}
              />
            </div>
            <div className='flex justify-end gap-2'>
              <SharedButton
                type="button"
                icon='pi pi-times'
                label={t('Cancel')}
                buttonType="reset"
                onClick={() => {
                  navigate(-1)
                  formik.resetForm()
                }}
              />
              <SharedButton
                type="submit"
                icon={<FaCheckDouble size={12} className='me-2' />}
                className='submit-btn'
                loading={formik.isSubmitting}
                disabled={formik.isSubmitting}
                label={
                  formik.isSubmitting
                    ? t('submitting')
                    : editData
                    ? t('update')
                    : t('submit')
                }
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default PaymentMethodsPage
